/**
 * weighingOnScaleHelper.js
 *
 * Shared logic used by both the desktop weighing-on-scale wizard
 * (src/pages/scale/weighing-on-scale/_id.vue) and the mobile field-report
 * flow (src/pages/field-report-mobile/dialogs/partials/fieldReport.vue).
 *
 * The two surfaces save the same backend resource (POST / PUT
 * /weighing-on-scale) and share three concerns extracted here:
 *
 *  1. roundToDecimals        — rounding utility used in every weight split.
 *  2. divideWeightByParcels  — proportional surface split used by both
 *                              calculateWeighingProducts (mobile) and
 *                              getProductDivision Strategy B (desktop).
 *  3. extractProductFromWeighingProducts — single source-of-truth for
 *                              reading product fields back from a saved
 *                              record's `weighing_products` array; used on
 *                              load in fieldReport.vue (mapProductFromReport)
 *                              and in _id.vue's fetchWeighingOnScaleData.
 *                              recipient_warehouse_uuid and
 *                              issuer_warehouse_uuid are ONLY sourced from
 *                              weighing_products on reload — they are not
 *                              stored as flat fields on the parent record.
 *  4. extractRecipientWarehouseUuidsFromWeighingProducts — collects every
 *                              unique recipient_warehouse_uuid across ALL
 *                              weighing_products entries (not just [0]) so
 *                              that multi-warehouse splits are fully restored
 *                              into recipient_warehouse_uuids on load.
 *  5. buildWeighingProductsByWarehouseGroups — multi-warehouse proportional
 *                              split for the mobile field-report.  Groups
 *                              parcels by legal entity, maps each group to its
 *                              pre-assigned warehouse (from the loaded
 *                              weighing_products), and distributes weight
 *                              proportionally across groups then within each
 *                              group by parcel surface.  Mirrors the
 *                              buildProductDivisionFromWarehouseSplit logic
 *                              used by _id.vue but as a pure function.
 */

/**
 * Rounds a numeric value to a fixed number of decimal places.
 *
 * Coerces non-numeric input to 0 before rounding.
 *
 * @param {*}      value    - The value to round.
 * @param {number} decimals - Number of decimal places.
 * @returns {number}
 */
export function roundToDecimals(value, decimals) {
  if (typeof value !== "number") value = Number(value) || 0;
  return Number(value.toFixed(decimals));
}

/**
 * Distributes a total weight across an array of parcels proportionally by
 * surface area.
 *
 * Rules:
 *  - Every parcel except the last receives `(parcel.surface / totalSurface) * totalWeightKg`,
 *    rounded to `decimals` places.
 *  - The last parcel receives the residual (`totalWeightKg - sum of others`)
 *    so the grand total always matches `totalWeightKg` exactly.
 *  - When `totalWeightKg` is 0 (unfinished weighing) every division will be 0,
 *    but the entries are still created so product / warehouse / parcel
 *    associations are preserved in the payload.
 *   - When `totalSurface` is 0 (no surface data) the last parcel gets the full residual so the total still matches `totalWeightKg`.
 *
 * The caller is responsible for building the final product-division objects;
 * this function returns only the per-parcel weight values in the same order
 * as `parcels`.
 *
 * @param {Object[]} parcels      - Array of parcel objects with a numeric `surface` field.
 * @param {number}   totalWeightKg - Total net weight to distribute (kg); may be 0.
 * @param {number}   decimals      - Decimal places to round each division to.
 * @returns {number[]} Array of rounded per-parcel weight values (same length as `parcels`).
 */
export function divideWeightByParcels(parcels, totalWeightKg, decimals) {
  if (!Array.isArray(parcels) || parcels.length === 0) return [];

  const totalSurface = parcels.reduce((sum, p) => sum + (Number(p.surface) || 0), 0);
  const divisions = [];
  let assigned = 0;

  parcels.forEach((parcel, idx) => {
    let division = 0;
    if (idx < parcels.length - 1 && totalSurface > 0) {
      division = roundToDecimals(((Number(parcel.surface) || 0) / totalSurface) * (totalWeightKg || 0), decimals);
      assigned += division;
    } else if (idx === parcels.length - 1) {
      // Last parcel absorbs residual to prevent floating-point drift.
      division = roundToDecimals((totalWeightKg || 0) - assigned, decimals);
    }
    divisions.push(division);
  });

  return divisions;
}

/**
 * Extracts product identity and warehouse routing fields from a saved
 * weighing-on-scale record.
 *
 * IMPORTANT — source-of-truth contract:
 *   `recipient_warehouse_uuid` and `issuer_warehouse_uuid` live exclusively
 *   inside `record.weighing_products[0]` on the API response.  They are NOT
 *   stored as flat scalar fields on the parent record.  This function is the
 *   single place that reads them, so both desktop reload
 *   (fetchWeighingOnScaleData) and mobile reload (loadExistingFieldReport) use
 *   the same logic and no field is silently dropped.
 *
 *   Similarly, `product_uuid`, `product_name`, and `product_code` are
 *   authoritative from `weighing_products[0]` when available; the flat fields
 *   on the record are used only as fallback for older records that pre-date the
 *   weighing_products array.
 *
 * Returned shape (all fields nullable when missing):
 * ```
 * {
 *   product_uuid:             string | null,
 *   product_name:             string | null,
 *   product_code:             string | null,
 *   label_uuid:               string | null,
 *   recipient_warehouse_uuid: string | null,  // from weighing_products[0] ONLY
 *   issuer_warehouse_uuid:    string | null,  // from weighing_products[0] ONLY
 *   measure_unit_id:          * | null,
 *   measure_unit:             Object | null,
 *   product_type_uuid:        string | null,
 *   product_type:             Object | null,
 *   unit_price:               number,
 *   quantity:                 number,         // estimated_weight_kg
 *   notes:                    string,
 *   valid_measureunits_for_product: Array,
 * }
 * ```
 *
 * @param {Object} record - API response data object (the `response.data` value).
 * @returns {Object} Extracted and normalised product + routing fields.
 */
export function extractProductFromWeighingProducts(record) {
  if (!record) {
    return {
      product_uuid: null,
      product_name: null,
      product_code: null,
      label_uuid: null,
      recipient_warehouse_uuid: null,
      issuer_warehouse_uuid: null,
      measure_unit_id: null,
      measure_unit: null,
      product_type_uuid: null,
      product_type: null,
      unit_price: 0,
      quantity: 0,
      notes: "",
      valid_measureunits_for_product: [],
    };
  }

  // weighing_products is authoritative; fall back to legacy flat fields.
  const wp = Array.isArray(record.weighing_products) && record.weighing_products.length > 0
    ? record.weighing_products[0]
    : null;

  return {
    // Product identity — wp takes priority over flat record fields.
    product_uuid:             wp?.product_uuid             || record.product_uuid             || null,
    product_name:             wp?.product_name             || record.product_name             || null,
    product_code:             wp?.product_code             || record.product_code             || null,
    label_uuid:               wp?.label_uuid               || record.label_uuid               || null,
    // Routing fields — ONLY from weighing_products; not present as flat fields.
    recipient_warehouse_uuid: wp?.recipient_warehouse_uuid ?? null,
    issuer_warehouse_uuid:    wp?.issuer_warehouse_uuid    ?? null,
    // Measure-unit data — wp first, then record.
    measure_unit_id:          wp?.measure_unit_id          || record.measure_unit_id          || null,
    measure_unit:             wp?.measure_unit             || record.measure_unit             || null,
    // Product type.
    product_type_uuid:        wp?.product_type_uuid        || record.product_type_uuid        || null,
    product_type:             wp?.product_type             || record.product_type             || null,
    // Pricing / quantity / notes.
    unit_price:               wp?.unit_price               ?? 0,
    quantity:                 wp?.quantity                 ?? record.estimated_weight_kg      ?? 0,
    lot_number:               wp?.lot_number               ?? null,
    notes:                    wp?.notes                    ?? record.notes                    ?? "",
    valid_measureunits_for_product:
      wp?.valid_measureunits_for_product || record.valid_measureunits_for_product || [],
  };
}

/**
 * Maps the selected_parcels / selectedParcels array from an API response to
 * a plain array of parcel objects, normalising the two possible key names used
 * by different API versions.
 *
 * @param {Object} record - API response data object.
 * @returns {Object[]} Array of parcel objects (may be empty).
 */
export function extractParcelsFromRecord(record) {
  return record?.selected_parcels || record?.selectedParcels || [];
}

/**
 * Collects every unique `recipient_warehouse_uuid` value from ALL entries in
 * `record.weighing_products`, preserving insertion order.
 *
 * Used on record load to restore `recipient_warehouse_uuid` (first entry) and
 * `recipient_warehouse_uuids` (every distinct warehouse) so that multi-
 * warehouse splits are fully reflected in the load-step UI selectors.
 *
 * Unlike `extractProductFromWeighingProducts` which only reads index 0, this
 * function iterates the full array because each weighing_products entry can
 * reference a different recipient warehouse (one per legal-entity / parcel
 * group after a warehouse split).
 *
 * @param {Object} record - API response data object.
 * @returns {string[]} Ordered array of unique recipient_warehouse_uuid strings
 *                     (empty when weighing_products is absent or all entries
 *                     have no UUID).
 */
export function extractRecipientWarehouseUuidsFromWeighingProducts(record) {
  if (!Array.isArray(record?.weighing_products) || record.weighing_products.length === 0) {
    return [];
  }

  const seen = new Set();
  const uuids = [];
  record.weighing_products.forEach((wp) => {
    const uuid = wp?.recipient_warehouse_uuid;
    if (uuid && !seen.has(uuid)) {
      seen.add(uuid);
      uuids.push(uuid);
    }
  });
  return uuids;
}

/**
 * Builds a `weighing_products` array for a multi-warehouse field-report save,
 * mirroring the desktop `buildProductDivisionFromWarehouseSplit` logic as a
 * pure function.
 *
 * Designed for the mobile flow where the loaded record already contains
 * per-parcel warehouse assignments inside `weighing_products`.  The caller
 * supplies a `parcelWarehouseMap` (parcelUuid → warehouseUuid) built from
 * those entries so no Vuex warehouse store access is needed.
 *
 * Algorithm:
 *  1. Groups parcels by legal entity using `getLegalEntityUuid`.
 *     Within each group, the warehouse UUID is resolved from `parcelWarehouseMap`
 *     by scanning the group's parcels — first match wins.
 *  2. Distributes `totalWeightKg` across groups proportionally by their
 *     combined surface area.  The last group absorbs any rounding residual.
 *  3. Within each group, distributes the group weight across individual
 *     parcels proportionally by surface using `divideWeightByParcels`.
 *  4. Each output entry carries the product fields, the group's warehouse,
 *     the parcel's own UUID / surface / season, and the per-parcel weight.
 *
 * @param {Object}   params
 * @param {Object[]} params.parcels            - All selected parcels (selectedLayers).
 * @param {Object}   params.parcelWarehouseMap - Map of { [parcelUuid]: warehouseUuid }
 *                                              built from existing weighing_products.
 * @param {Function} params.getLegalEntityUuid - (parcel) => string|null  — resolves
 *                                              the owning legal-entity UUID from a parcel.
 * @param {Function} params.getParcelUuid      - (parcel) => string       — resolves the
 *                                              parcel UUID used as the payload key.
 * @param {Function} params.getSeasonOnParcelUuid - (parcel) => string|null
 * @param {number}   params.totalWeightKg      - Total net weight in kg (may be 0).
 * @param {number}   params.decimals           - Decimal places for rounding.
 * @param {string}   params.product_uuid
 * @param {string}   params.product_name
 * @param {string}   params.product_code
 * @param {*}        params.measure_unit_id
 * @param {string}   params.product_type_uuid
 * @param {number}   params.unit_price
 * @param {string|null} params.issuer_warehouse_uuid
 * @param {string|null} params.partner_uuid
 * @param {string|null} params.contract_uuid
 * @returns {Object[]} Array of weighing-product entries for the API payload.
 */
export function buildWeighingProductsByWarehouseGroups({
  parcels,
  parcelWarehouseMap,
  getLegalEntityUuid,
  getParcelUuid,
  getSeasonOnParcelUuid,
  totalWeightKg,
  decimals,
  product_uuid,
  product_name,
  product_code,
  measure_unit_id,
  product_type_uuid,
  unit_price,
  issuer_warehouse_uuid,
  partner_uuid,
  contract_uuid,
}) {
  // Step 1: group parcels by legal entity and resolve each group's warehouse.
  const groups = {};
  const groupOrder = []; // preserve insertion order

  parcels.forEach((parcel) => {
    const legalEntityUuid = getLegalEntityUuid(parcel);
    if (!legalEntityUuid) return;

    if (!groups[legalEntityUuid]) {
      groups[legalEntityUuid] = { parcels: [], total_surface: 0, warehouse_uuid: null };
      groupOrder.push(legalEntityUuid);
    }

    groups[legalEntityUuid].parcels.push(parcel);
    groups[legalEntityUuid].total_surface += Number(parcel.surface) || 0;

    // First parcel in the group that has a warehouse assignment wins.
    if (!groups[legalEntityUuid].warehouse_uuid) {
      groups[legalEntityUuid].warehouse_uuid = parcelWarehouseMap[getParcelUuid(parcel)] || null;
    }
  });

  if (groupOrder.length === 0) return [];

  // Step 2: proportionally distribute totalWeightKg across groups by surface.
  const grandTotalSurface = groupOrder.reduce((sum, uuid) => sum + groups[uuid].total_surface, 0);
  const productDivision = [];
  let assignedGroupTotal = 0;

  groupOrder.forEach((legalEntityUuid, groupIndex) => {
    const group = groups[legalEntityUuid];
    const groupSurface = group.total_surface || 0;

    const total = Number(totalWeightKg) || 0;

    let groupWeight = 0;
    if (grandTotalSurface > 0 && groupIndex < groupOrder.length - 1) {
       groupWeight = roundToDecimals((groupSurface / grandTotalSurface) * total, decimals);
       assignedGroupTotal += groupWeight;
    } else if (groupIndex === groupOrder.length - 1) {
       // Last group (or no surface data) absorbs residual to keep totals consistent.
       groupWeight = roundToDecimals(total - assignedGroupTotal, decimals);
     }

    // Step 3: distribute group weight across parcels by surface.
    const parcelDivisions = divideWeightByParcels(group.parcels, groupWeight, decimals);

    group.parcels.forEach((parcel, parcelIdx) => {
      productDivision.push({
        product_uuid,
        product_name,
        product_code,
        measure_unit_id,
        product_type_uuid,
        unit_price,
        // Each group gets its own warehouse (the one associated with its legal entity).
        recipient_warehouse_uuid: group.warehouse_uuid,
        issuer_warehouse_uuid,
        partner_uuid,
        season_on_parcel_uuid: getSeasonOnParcelUuid(parcel),
        parcel_uuid: getParcelUuid(parcel),
        surface: parcel.surface,
        netto_weight_kg: parcelDivisions[parcelIdx],
        contract_uuid,
      });
    });
  });

  return productDivision;
}
